import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn.js';

const CONTROL = cn(
  'w-full rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-3 text-[var(--ink)]',
  'outline-none transition-[border-color,box-shadow] duration-[var(--dur-fast)]',
  'placeholder:text-[var(--ink-3)]',
  'focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]',
  'disabled:cursor-not-allowed disabled:bg-[var(--surface-2)] disabled:text-[var(--ink-3)]',
  'aria-[invalid=true]:border-[var(--debit)] aria-[invalid=true]:ring-[var(--debit-soft)]',
);

const HEIGHT = {
  sm: 'h-8 text-[12.5px]',
  md: 'h-[38px] text-[13.5px]',
  lg: 'h-[42px] text-[14px]',
};

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required, htmlFor, className, children }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-[7px]', className)}>
      {label && (
        <label htmlFor={htmlFor} className="bb-eyebrow flex items-center gap-1">
          {label}
          {required && (
            <span className="text-[var(--debit)]" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <span className="text-[11.5px] text-[var(--debit)]">{error}</span>
      ) : hint ? (
        <span className="text-[11.5px] text-[var(--ink-3)]">{hint}</span>
      ) : null}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: keyof typeof HEIGHT;
  invalid?: boolean;
  numeric?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid, numeric, leading, trailing, className, ...rest },
  ref,
) {
  const control = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      inputMode={numeric ? 'decimal' : undefined}
      className={cn(
        CONTROL,
        HEIGHT[size],
        numeric && 'bb-num text-right',
        leading && 'pl-12',
        trailing && 'pr-12',
        className,
      )}
      {...rest}
    />
  );

  if (!leading && !trailing) return control;

  return (
    <div className="relative flex items-center">
      {leading && (
        <span className="pointer-events-none absolute left-3 text-[12px] text-[var(--ink-3)]">
          {leading}
        </span>
      )}
      {control}
      {trailing && (
        <span className="pointer-events-none absolute right-3 text-[12px] text-[var(--ink-3)]">
          {trailing}
        </span>
      )}
    </div>
  );
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: keyof typeof HEIGHT;
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid, className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(CONTROL, HEIGHT[size], 'cursor-pointer appearance-none pr-8', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238b9aa0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
      }}
      {...rest}
    >
      {children}
    </select>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(CONTROL, 'resize-y py-2 text-[13.5px] leading-relaxed', className)}
      {...rest}
    />
  );
});

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'children'
> {
  label?: ReactNode;
  description?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;

  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        className={cn(
          'mt-0.5 size-[15px] shrink-0 cursor-pointer appearance-none rounded-[4px]',
          'border border-[var(--line)] bg-[var(--surface)]',
          'checked:border-[var(--accent-solid)] checked:bg-[var(--accent-solid)]',
          'focus-visible:ring-2 focus-visible:ring-[var(--accent-soft)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='8' viewBox='0 0 10 8' fill='none'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '10px 8px',
        }}
        {...rest}
      />
      {(label || description) && (
        <label htmlFor={inputId} className="cursor-pointer select-none">
          {label && <span className="text-[13px] text-[var(--ink)]">{label}</span>}
          {description && (
            <span className="block pt-0.5 text-[11.5px] text-[var(--ink-3)]">{description}</span>
          )}
        </label>
      )}
    </div>
  );
});

export interface KeyValueItem {
  label: ReactNode;
  value: ReactNode;
  mono?: boolean;
  tone?: 'default' | 'strong';
}

export function KeyValue({ items, className }: { items: KeyValueItem[]; className?: string }) {
  return (
    <dl className={cn('flex flex-col', className)}>
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-baseline justify-between gap-3.5 border-b border-[var(--line-2)] py-2 last:border-b-0"
        >
          <dt className="shrink-0 text-[12px] whitespace-nowrap text-[var(--ink-3)]">
            {item.label}
          </dt>
          <dd
            className={cn(
              'm-0 min-w-0 text-right text-[12.5px] [text-wrap:pretty] text-[var(--ink)]',
              item.tone === 'strong' ? 'font-semibold' : 'font-medium',
              item.mono && 'bb-num',
            )}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
